import { $formats, UIParagraphFormat } from '../../config/formats';
import { uiGetPrefixedClass, uiSetPrefixedClass } from './functions';
import { UIClassesSpec } from './types';

export interface UIParagraphFormatSpec {
    name: string;
    classes?: UIClassesSpec;
    text?: { [key in UITextStyleParam]?: UITextStyleTypes[key] };
}

export interface UIParagraphStyleTypes {
    f: UIParagraphFormat;
    a: 'left' | 'right' | 'center' | 'justify';
    l: '1'
}

export type UIParagraphStyleParam = keyof UIParagraphStyleTypes;

export interface UITextStyleTypes {
    c: 'fg' | 'hard' | 'soft' | 'tint' | 'secondary';
    s: 'normal' | 'italic';
    w: 'regular' | 'medium' | 'bold' | 'black';
    underline: boolean;
    strike: boolean;
}

export type UITextStyleParam = keyof UITextStyleTypes;

export namespace $style {
    interface TextStyleSpec<P extends UITextStyleParam> {
        def: UITextStyleTypes[P];
    }

    const text_style_specs: Record<UITextStyleParam, TextStyleSpec<UITextStyleParam>> = {
        'c': { def: 'fg' },
        's': { def: 'normal' },
        'w': { def: 'regular' },
        'underline': { def: false },
        'strike': { def: false }
    }

    function getParagraphTextStyle<P extends UITextStyleParam>(ref: Element, root: Element, style: P): UITextStyleTypes[P] | undefined {
        let node: Element | null = ref;
        while(node && node != root) {
            const v = $formats[uiGetPrefixedClass(node, 'p-f') as UIParagraphFormat || 'normal']?.text?.[style];
            if(v) return v;
            node = node.parentElement;
        }
        return undefined;
    }

    export function spanHas<P extends UITextStyleParam>(span: Element | undefined, ref: Element | undefined, root: Element, style: P, value: UITextStyleTypes[P] | undefined) {
        return spanGet(span, ref, root, style) == (value ?? text_style_specs[style].def as UITextStyleTypes[P]);
    }

    export function spanSet<P extends UITextStyleParam>(span: Element, root: Element, style: P, value: UITextStyleTypes[P] | undefined) {
        const def = text_style_specs[style].def;
        const paragraph = getParagraphTextStyle(span, root, style) || def;
        value = value || def as UITextStyleTypes[P];

        if(style == 'c' || style == 'w' || style == 's') {
            uiSetPrefixedClass(span, 't-' + style, value == paragraph ? undefined : value as string);
        } else if(style == 'underline' || style == 'strike') {
            span.classList.toggle('t-' + style, !!value);
        }
    }

    export function spanGet<P extends UITextStyleParam>(span: Element | undefined, ref: Element | undefined, root: Element, style: P): UITextStyleTypes[P] {
        if(style == 'c' || style == 'w' || style == 's')
            return ((span && uiGetPrefixedClass(span, 't-' + style)) || (ref && getParagraphTextStyle(ref, root, style)) || text_style_specs[style].def) as UITextStyleTypes[P];
        else
            return ((span && span.classList.contains('t-' + style)) || (ref && getParagraphTextStyle(ref, root, style)) || text_style_specs[style].def) as UITextStyleTypes[P];
    }
}